// Per-service discount math + config→fraud mapping.
// Jose 2026-08-09: discounts are CONFIGURED in Settings (type $ / % / comp + value), CHOSEN per
// service in the appointment card (instant strikethrough), and the existing checkout fraud gate
// still runs — "I don't want things to change, just where it is." The fraud is about barbers
// pocketing CASH / SLIK coins, not cards. See project-slik-pos-punch-list-2026-08-07 (#5).

import type { DiscountReason as FraudDiscountReason } from '../services/fraudDetectionService';

export type DiscountType = 'comp' | 'percent' | 'fixed';

/** A discount as CHOSEN for one service — stored on the appointment so it flows to checkout. */
export interface ServiceDiscount {
  serviceName: string;
  originalPrice: number;
  discountedPrice: number;
  discountAmount: number;   // originalPrice - discountedPrice (always >= 0)
  reasonId: string;         // config id (e.g. 'complimentary', 'ten_off')
  reasonLabel: string;      // human label for the receipt / display
  type: DiscountType;
  value: number;            // 100 for comp, the % for percent, the $ for fixed
}

/** Round to cents to avoid float drift on the money path. */
const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Apply a configured discount to an original price.
 *  - comp    → free (0)
 *  - percent → original * (1 - value/100)
 *  - fixed   → original - value (never below 0)
 */
export function computeDiscountedPrice(original: number, type: DiscountType, value: number): number {
  if (!original || original <= 0) return 0;
  switch (type) {
    case 'comp':
      return 0;
    case 'percent':
      return round2(Math.max(0, original * (1 - (value || 0) / 100)));
    case 'fixed':
      return round2(Math.max(0, original - (value || 0)));
    default:
      return original;
  }
}

/** Build the stored ServiceDiscount record for one service selection. */
export function buildServiceDiscount(
  serviceName: string,
  originalPrice: number,
  reason: { id: string; label: string; type?: DiscountType; value?: number }
): ServiceDiscount {
  const type = reason.type || 'fixed';
  const value = reason.value ?? 0;
  const discountedPrice = computeDiscountedPrice(originalPrice, type, value);
  return {
    serviceName,
    originalPrice: round2(originalPrice),
    discountedPrice,
    discountAmount: round2(Math.max(0, originalPrice - discountedPrice)),
    reasonId: reason.id,
    reasonLabel: reason.label,
    type,
    value,
  };
}

/**
 * Map a config reason id → the narrower fraud-service DiscountReason union so the EXISTING fraud
 * gate keeps working unchanged. Ids the union already knows pass through; everything else → 'other'.
 */
export function mapReasonToFraud(reasonId: string): FraudDiscountReason {
  switch (reasonId) {
    case 'unsatisfied_client':
    case 'service_issue':
    case 'loyalty':
    case 'grandfather_pricing':
      return reasonId;
    case 'compensation':
      return 'service_issue'; // compensation for an error ≈ service issue
    default:
      return 'other'; // complimentary, family_member, ten_off, five_off, promotion, other, campaign codes…
  }
}

/** Sum the discount amounts across every per-service selection. */
export function totalDiscountAmount(discounts: ServiceDiscount[] | undefined): number {
  if (!discounts || discounts.length === 0) return 0;
  return round2(discounts.reduce((sum, d) => sum + (d.discountAmount || 0), 0));
}
